<script setup lang="ts">
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { map, get, isString, isFunction } from 'lodash';
import type { TableColumn, Column, Cell, CellValue, Row } from './types';
import ItpTableCell from './ItpTableCell.vue';

const props = withDefaults(
  defineProps<{
    rows: object[];
    columns: TableColumn[];
    bordered?: boolean;
    size?: string;
    headerVariant?: string;
  }>(),
  {
    bordered: true,
    size: 'sm',
    headerVariant: 'light',
  },
);

const instance = getCurrentInstance();
const $filters = computed(
  () => instance?.appContext.config.globalProperties.$filters,
);

function getColumnClassName(column: TableColumn): string | string[] {
  if (!props.bordered) {
    return ['text-center', 'border-top-0'];
  }
  return 'text-center';
}

function getCellValue(
  column: TableColumn,
  row: Record<string, any>,
): CellValue {
  const value = get(row, column.field);
  if (
    column.type &&
    $filters.value &&
    isFunction($filters.value[column.type])
  ) {
    return $filters.value[column.type](value) as CellValue;
  }

  return value as CellValue;
}

function getCellClassName(
  column: TableColumn,
  row: Record<string, any>,
): string[] {
  const result: string[] = [];

  if (isString(row['$rowClass'])) {
    result.push(row['$rowClass']);
  }

  if (column.textAlign) {
    result.push(`text-${column.textAlign}`);
  }

  return result;
}

function getTableColumns(): Column[] {
  return map(props.columns, (col) => ({
    ...col,
    className: getColumnClassName(col),
  }));
}

function getTableRows(): Row[] {
  return map(props.rows, (row, i) => ({
    rowIndex: i,
    cells: getRowCells(row as Record<string, any>),
    className: null,
  }));
}

function getRowCells(rowData: Record<string, any>): Cell[] {
  return map(props.columns, (column, i) => ({
    cellIndex: i,
    column,
    rowData,
    value: getCellValue(column, rowData),
    className: getCellClassName(column, rowData),
  }));
}

const tableColumns = ref<Column[]>(getTableColumns());
const tableRows = ref<Row[]>(getTableRows());

watch(
  () => props.rows,
  () => {
    tableColumns.value = getTableColumns();
    tableRows.value = getTableRows();
  },
);

watch(
  () => props.columns,
  () => {
    tableColumns.value = getTableColumns();
    tableRows.value = getTableRows();
  },
);

const className = computed(() => [
  'table',
  `table-${props.size}`,
  {
    'table-bordered': props.bordered,
  },
]);

const theadClassName = computed(() => {
  if (props.headerVariant) {
    return `table-${props.headerVariant}`;
  }
  return undefined;
});
</script>

<template>
  <table :class="className">
    <thead :class="theadClassName">
      <tr>
        <th
          v-for="col in tableColumns"
          :key="col.field"
          scope="col"
          :class="col.className"
        >
          {{ col.name }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="row in tableRows" :key="row.rowIndex" :class="row.className">
        <ItpTableCell
          v-for="cell in row.cells"
          :key="cell.cellIndex"
          :row="row"
          :cell="cell"
        />
      </tr>
    </tbody>
  </table>
</template>
